import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { Grid } from "@material-ui/core";
import CustomModal from "../../components/common/customModal";
import CustomInput from "../../components/common/inputField";
import LogoUpload from "../../components/common/logoUpload";
import { UseFullLinks } from "../../types/dashboard";
import dashboardService from "../../services/DashboardServices";
import { showToast } from "../../components/common/toastNotification";

interface UseFullLinksModalsProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  useFullLinksUpdateData?: UseFullLinks;
}

const UseFullLinksModals: React.FC<UseFullLinksModalsProps> = ({
  open,
  onClose,
  onSave,
  useFullLinksUpdateData,
}) => {
  useEffect(() => {
    if (useFullLinksUpdateData) {
      setFormData({
        id: useFullLinksUpdateData.id || null,
        title: useFullLinksUpdateData.title || "",
        logoUrl: useFullLinksUpdateData.logoUrl || "",
        Logo: null,
        spaceLink: useFullLinksUpdateData.spaceLink || "",
      });
    }
  }, [useFullLinksUpdateData]);

  const [formData, setFormData] = useState<UseFullLinks>({
    id: null,
    title: "",
    logoUrl: "",
    spaceLink: "",
    Logo: null,
  });
  const [formErrors, setFormErrors] = useState<UseFullLinks>({
    title: "",
    spaceLink: "",
    logoUrl: "",
    Logo: null,
  });
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setFormErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setFormData((prev) => ({ ...prev, Logo: file }));
    if (file) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        logoUrl: "",
      }));
    }
  };

  // validation
  const validateForm = () => {
    const errors: Partial<UseFullLinks> = {};
    const urlRegex =
      /^(https?:\/\/)(([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})(\/[^\s]*)?$/i;

    if (!formData.title?.trim()) {
      errors.title = "Title is required.";
    }

    if (!formData.spaceLink?.trim()) {
      errors.spaceLink = "Google drive Link is required.";
    } else if (!urlRegex.test(formData.spaceLink.trim())) {
      errors.spaceLink = "Enter a valid URL for the SpaceLink.";
    }

    const logoUrl = formData.logoUrl?.trim() || "";

    if (!logoUrl && !formData.Logo) {
      errors.logoUrl = "Either a file or a URL must be provided.";
      showToast(errors.logoUrl, "error");
    } else if (logoUrl && !urlRegex.test(logoUrl)) {
      errors.logoUrl = "Enter a valid URL for the Logo.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // add and update  api calling
  const handleSaveData = async () => {
    if (!validateForm()) return;

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("Id", formData.id?.toString() || "0");
      formDataToSend.append("Title", formData.title?.trim() || "");
      formDataToSend.append("SpaceLink", formData.spaceLink?.trim() || "");

      if (formData.Logo instanceof File) {
        formDataToSend.append("Logo", formData.Logo);
        formDataToSend.append("LogoUrl", "");
      } else if (formData.logoUrl?.trim()) {
        formDataToSend.append("LogoUrl", formData.logoUrl.trim());
        formDataToSend.append("Logo", "");
      }

      const apiCall = formData.id
        ? dashboardService.updateUseFullLinksData(formDataToSend)
        : dashboardService.addUseFullLinksData(formDataToSend);

      await apiCall.then((response) => {
        if (response && response.data) {
          showToast(response.data.message, "success");
          setFormData({
            id: 0,
            title: "",

            spaceLink: "",
            logoUrl: "",
            Logo: null,
          });
          onSave();
          onClose();
        } else {
          showToast(response?.data?.message || "Error occurred", "error");
        }
      });
    } catch (error) {
      console.error("Error:", error);
    }
  };
  return (
    <CustomModal
      open={open}
      handleClose={onClose}
      onSave={handleSaveData}
      title="Useful Links"
    >
      <Box>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <CustomInput
              label="Title"
              type="text"
              name="title"
              placeholder="Enter Title"
              value={formData.title || ""}
              onChange={handleChange}
              error={formErrors.title}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <LogoUpload
              label="Upload Logo"
              onChange={handleFileUpload}
              fileName={formData.Logo instanceof File ? formData.Logo.name : ""}
            />
          </Grid>
          <Grid item xs={12} md={9}>
            <CustomInput
              label="Upload logo via the URL"
              type="text"
              name="logoUrl"
              placeholder="Enter URL"
              value={formData.logoUrl || ""}
              onChange={handleChange}
              error={formErrors.logoUrl}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomInput
              label="URL of the google drive Link"
              type="text"
              name="spaceLink"
              placeholder="Enter the URL of the google drive Link"
              value={formData.spaceLink || ""}
              onChange={handleChange}
              error={formErrors.spaceLink}
            />
          </Grid>
        </Grid>
      </Box>
    </CustomModal>
  );
};

export default UseFullLinksModals;
